import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { KeyRound, Eye, EyeOff, Loader2 } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

/**
 * ChangePasswordDialog — focused modal that wraps `/api/auth/change-password`.
 *
 * Backend takes (current_password, new_password) as query params (legacy), so
 * we forward them that way to avoid breaking the contract.
 */
export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const reset = () => { setCurrent(''); setNext(''); setConfirm(''); setShowPwd(false); };

  const submit = async (e) => {
    e?.preventDefault();
    if (!current || !next) {
      toast.error('Please fill in both passwords.');
      return;
    }
    if (next !== confirm) {
      toast.error('New passwords don\'t match.');
      return;
    }
    if (next.length < 8) {
      toast.error('New password must be at least 8 characters.');
      return;
    }
    if (next === current) {
      toast.error('New password must be different from current.');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/auth/change-password`,
        null,
        {
          params: { current_password: current, new_password: next },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success('Password updated. Use the new one next time you sign in.');
      reset();
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not change password.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const fieldClass =
    'w-full px-3 py-2 rounded-lg bg-[#0B1321] border border-[#2D3748] text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 placeholder:text-[#475569]';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent
        className="max-w-md bg-[#0F1923] border border-[#2D3748] text-white"
        data-testid="change-password-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <KeyRound className="w-5 h-5" style={{ color: '#1E88E5' }} />
            Change Password
          </DialogTitle>
          <DialogDescription className="text-sm" style={{ color: '#94A3B8' }}>
            Pick a strong password (min. 8 characters). You'll stay signed in on this device.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <label className="text-xs font-medium block mb-1" style={{ color: '#94A3B8' }}>
              Current password
            </label>
            <input
              type={showPwd ? 'text' : 'password'}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className={fieldClass}
              data-testid="cp-current"
              autoComplete="current-password"
              required
            />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1" style={{ color: '#94A3B8' }}>
              New password
            </label>
            <input
              type={showPwd ? 'text' : 'password'}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className={fieldClass}
              data-testid="cp-new"
              autoComplete="new-password"
              required
            />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1" style={{ color: '#94A3B8' }}>
              Confirm new password
            </label>
            <input
              type={showPwd ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={fieldClass}
              data-testid="cp-confirm"
              autoComplete="new-password"
              required
            />
          </div>

          <button
            type="button"
            onClick={() => setShowPwd((v) => !v)}
            className="text-xs flex items-center gap-1.5 hover:underline"
            style={{ color: '#94A3B8' }}
            data-testid="cp-toggle-visibility"
          >
            {showPwd ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {showPwd ? 'Hide' : 'Show'} passwords
          </button>

          <DialogFooter className="pt-2">
            <button
              type="button"
              onClick={() => { reset(); onOpenChange(false); }}
              className="px-4 py-2 rounded-lg text-sm font-medium border transition-colors"
              style={{ borderColor: '#2D3748', background: 'transparent', color: '#E2E8F0' }}
              data-testid="cp-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 flex items-center gap-2"
              style={{ background: '#1E88E5', color: '#fff' }}
              data-testid="cp-submit"
            >
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {submitting ? 'Updating…' : 'Update password'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
